import type { AxiosInstance, InternalAxiosRequestConfig } from "axios"
import axios from "axios"

import { setAuthTokens, setIsUserAuthenticated, setShouldAuthenticate } from "../features/Auth/Slice"
import { store } from "../store/ReduxStore"
import { getHostnames } from "../utils/genericHelpers"
import type { ApiConfig, ApiFunctions, ApiMethodWithParams, ApiMethodWithoutParams } from "./axiosInterfaces"

/**
 * Creates a basic Axios instance with default configuration
 */
const createAxiosInstance = (): AxiosInstance => {
  return axios.create({
    paramsSerializer: {
      indexes: null,
    },
    xsrfCookieName: "csrftoken",
    xsrfHeaderName: "X-CSRFToken",
  })
}

/**
 * Sets up request interceptors
 */
const setupRequestInterceptors = (axiosInstance: AxiosInstance): void => {
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const authToken = store.getState().auth.authToken

      if (authToken && config.headers) {
        config.headers["Authorization"] = `Bearer ${authToken}`
      }
      return config
    },
    (error) => {
      Promise.reject(error).catch(console.error)
    },
  )
}

/**
 * Sets up response interceptors
 */
const setupResponseInterceptors = (axiosInstance: AxiosInstance, authUrl: string): void => {
  axiosInstance.interceptors.response.use(
    (response) => {
      return response
    },
    function (error) {
      const originalRequest = error.config
      const refreshEndpoint = "/api/auth/token/refresh/"

      // Handle failed refresh attempts
      if (
        (error.response?.status === 400 || error.response?.status === 403) &&
        originalRequest.url === authUrl + refreshEndpoint
      ) {
        // Clear auth state
        store.dispatch(setAuthTokens({ access: undefined, refresh: undefined }))
        store.dispatch(setIsUserAuthenticated(false))

        return Promise.reject(error)
      }

      // Handle 401 errors with token refresh
      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest?.url.includes("/api/auth/login/")
      ) {
        originalRequest._retry = true
        const refreshToken = store.getState().auth.refreshToken

        return axiosInstance
          .post(authUrl + refreshEndpoint, null, { withXSRFToken: true, withCredentials: true })
          .then((res) => {
            if (res.status === 200) {
              try {
                store.dispatch(setAuthTokens({ access: res.data.access, refresh: refreshToken }))
                store.dispatch(setIsUserAuthenticated(true))
                store.dispatch(setShouldAuthenticate(false))
              } catch (e) {
                return Promise.reject(error)
              }

              // Update the axios instance with the new token
              axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${res.data.access}`

              return axiosInstance(originalRequest)
            }
          })
      }

      return Promise.reject(error)
    },
  )
}

/**
 * Initializes the API configuration with Axios instance and interceptors
 */
const initializeApi = (): ApiConfig => {
  const axiosInstance = createAxiosInstance()

  // Get hostnames
  const { auth } = getHostnames()
  const AUTH_URL = `https://${auth}`

  setupRequestInterceptors(axiosInstance)
  setupResponseInterceptors(axiosInstance, AUTH_URL)

  return {
    axiosInstance,
    authUrl: AUTH_URL,
  }
}

/**
 * Creates API function wrappers for HTTP methods
 */
const createApiFunctions = (config: ApiConfig): ApiFunctions => {
  const { axiosInstance } = config

  const fetchResource: ApiMethodWithParams = (serviceUrl, endpoint, params = {}, config = {}) => {
    return axiosInstance.get(`https://${serviceUrl}${endpoint}`, {
      ...config,
      params,
    })
  }

  const postResource: ApiMethodWithParams = (serviceUrl, endpoint, params = {}, config = {}) => {
    return axiosInstance.post(`https://${serviceUrl}${endpoint}`, params, config)
  }

  const deleteResource: ApiMethodWithoutParams = (serviceUrl, endpoint, config = {}) => {
    return axiosInstance.delete(`https://${serviceUrl}${endpoint}`, config)
  }

  const patchResource: ApiMethodWithParams = (serviceUrl, endpoint, params = {}, config = {}) => {
    return axiosInstance.patch(`https://${serviceUrl}${endpoint}`, params, config)
  }

  const putResource: ApiMethodWithParams = (serviceUrl, endpoint, params = {}, config = {}) => {
    return axiosInstance.put(`https://${serviceUrl}${endpoint}`, params, config)
  }

  return {
    fetchResource,
    postResource,
    deleteResource,
    patchResource,
    putResource,
  }
}

// create an API client instance
const config = initializeApi()

const apiFunctions = createApiFunctions(config)

export const apiClient = {
  ...apiFunctions,
  config,
}
